// Take the hook from `/data`, NOT the `@heidi/agents` barrel: that door re-exports
// `client` (-> scribeHTTP -> authModel), so a VALUE import from it pulls that chain
// into this hook's module graph and, combined with a test's `requireActual`, loops
// authModel before init ('Cannot access auth before initialization').
import { useIngestComputerUseNotePushCandidatesCore } from '@heidi/agents/data';
import { useWorkspaceId } from '@heidi/workspace/data';
// Keep this light ingest hook off the note-push React/query graph.
import {
  clearCandidateMappingsAtom,
  ingestFieldCandidatesAtom,
  ingestNotePushHintsAtom,
  ingestNotePushRecordedAtom,
} from '@/features/computer-use-note-push/candidate-mappings';
// Leaf import (NOT the query hook) so this value import doesn't drag the query
// hook's placement-store → scribeHTTP → authModel chain into ProjectChat's graph.
import { NOTE_PUSH_TARGETS_QUERY_KEY } from '@/hooks/query/note-push-targets-query-key';
import { useQueryClient } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { useCallback } from 'react';

/**
 * Headless: feed note-push tool results off the project timeline into the
 * note-push candidate store, bound to the app's store writes, the workspace the
 * store keys under, and the stored-placements GET's invalidation.
 */
export const useIngestComputerUseNotePushCandidates = (
  args: Parameters<typeof useIngestComputerUseNotePushCandidatesCore>[0]
) => {
  const ingestHints = useSetAtom(ingestNotePushHintsAtom);
  const ingestRecorded = useSetAtom(ingestNotePushRecordedAtom);
  const ingestFieldCandidates = useSetAtom(ingestFieldCandidatesAtom);
  const clearCandidates = useSetAtom(clearCandidateMappingsAtom);
  // Workspace the record was stored under — scopes the placement-store GET key.
  const workspaceId = useWorkspaceId();
  const queryClient = useQueryClient();
  const invalidateTargets = useCallback(
    (templateId: string) => {
      void queryClient.invalidateQueries({
        queryKey: [NOTE_PUSH_TARGETS_QUERY_KEY, workspaceId, templateId],
      });
    },
    [queryClient, workspaceId]
  );

  useIngestComputerUseNotePushCandidatesCore(args, {
    workspaceId,
    ingestHints,
    ingestRecorded,
    ingestFieldCandidates,
    clearCandidates,
    invalidateTargets,
  });
};
